import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Edit2, Trash2, MapPin, Camera, X, BookOpen, Sparkles,
  ChevronDown, ChevronRight, ScanLine,
} from 'lucide-react';
import PrimaryButton from '../components/PrimaryButton';
import BottomSheet from '../components/BottomSheet';
import PhotoUploader from '../components/PhotoUploader';
import SeasonalPhotoStrip from '../components/SeasonalPhotoStrip';
import { plantsApi, photosApi, tasksApi, journalApi } from '../services/gardenApi';
import { useGardenPageTitle } from '../useGardenPageTitle';
import TaskRow from '../components/TaskRow';
import { displayBotanicalSubtitle, displayPrimaryPlantName } from '../utils/plantDisplayName';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const SEASONS = ['spring','summer','fall','winter'];

// Mirrors the field set in PlantFormScreen so we can show the user how many
// editable details are still blank. `common_name` is required and therefore
// never counted.
const EDITABLE_DETAIL_FIELDS = [
  'botanical_name', 'nickname', 'plant_type', 'variety',
  'bloom_color', 'bloom_month_start', 'bloom_month_end', 'foliage_notes', 'is_evergreen',
  'sun_needs', 'water_needs', 'soil_preference', 'hardiness_zone',
  'mature_height', 'mature_width',
  'prune_month', 'prune_notes', 'fertilise_month', 'divide_month', 'mulch_month',
  'dormancy_start', 'dormancy_end',
  'spring_tasks', 'summer_tasks', 'fall_tasks', 'winter_tasks',
  'location_notes', 'date_planted', 'health_status',
  'is_pet_safe', 'is_pollinator_friendly', 'is_native', 'is_drought_tolerant',
];

function monthRange(start, end) {
  if (!start && !end) return null;
  if (start && end && start !== end) return `${MONTHS[start-1]}–${MONTHS[end-1]}`;
  return MONTHS[(start || end) - 1];
}

function emojiFor(plantType) {
  switch ((plantType || '').toLowerCase()) {
    case 'tree': return '🌳';
    case 'shrub': return '🪴';
    case 'herb': return '🌿';
    case 'vegetable': return '🥬';
    case 'lawn': return '🌱';
    case 'annual': return '🌸';
    case 'perennial': return '🌼';
    default: return '🌿';
  }
}

function boolLabel(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return null;
}

export default function PlantDetailScreen() {
  const { plantId } = useParams();
  const navigate = useNavigate();

  const [plant, setPlant] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [marker, setMarker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [photoSheetSeason, setPhotoSheetSeason] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [ocrOpen, setOcrOpen] = useState(false);

  // Upcoming tasks for just this plant. Fetched once on mount and filtered
  // client-side; the tasks endpoint returns the whole garden in one shot,
  // which is still small for a home gardener.
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [taskPending, setTaskPending] = useState({});

  // Journal entries attached to this plant. Preview the most recent few here;
  // the full list lives on the Journal tab with a plant filter.
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalLoading, setJournalLoading] = useState(true);

  useGardenPageTitle(!plant || loading ? 'Plant' : displayPrimaryPlantName(plant), 'Plant');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await plantsApi.get(plantId);
      setPlant(r.plant);
      setPhotos(r.photos || []);
      setMarker(r.marker || null);
    } catch (e) {
      setError(e.message || 'Failed to load plant');
    } finally {
      setLoading(false);
    }
  }, [plantId]);

  useEffect(() => { load(); }, [load]);

  // Load upcoming tasks (next 3 months) and filter to this plant.
  useEffect(() => {
    let cancelled = false;
    setUpcomingLoading(true);
    (async () => {
      try {
        const r = await tasksApi.list('next_3m');
        if (cancelled) return;
        const mine = (r.tasks || []).filter((t) => t.plant_id === plantId);
        setUpcoming(mine);
      } catch {
        if (!cancelled) setUpcoming([]);
      } finally {
        if (!cancelled) setUpcomingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [plantId]);

  // Load journal entries filtered to just this plant. Re-fires when the user
  // navigates back from creating a new entry so the preview stays current.
  useEffect(() => {
    let cancelled = false;
    setJournalLoading(true);
    (async () => {
      try {
        const r = await journalApi.list({ plant_id: plantId });
        if (cancelled) return;
        setJournalEntries(r.entries || []);
      } catch {
        if (!cancelled) setJournalEntries([]);
      } finally {
        if (!cancelled) setJournalLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [plantId]);

  // Mirror TasksScreen's optimistic toggle. Kept local so we don't couple
  // the two screens; both dispatch the same cross-screen refresh event.
  const toggleTask = async (task) => {
    if (taskPending[task.key]) return;
    const next = !task.completed;
    setTaskPending((p) => ({ ...p, [task.key]: true }));
    setUpcoming((list) =>
      list.map((t) => (t.key === task.key ? { ...t, completed: next } : t))
    );
    try {
      if (next) await tasksApi.complete(task.key);
      else      await tasksApi.uncomplete(task.key);
      window.dispatchEvent(new Event('garden:tasks-changed'));
    } catch (e) {
      setUpcoming((list) =>
        list.map((t) => (t.key === task.key ? { ...t, completed: !next } : t))
      );
      alert(e.message || 'Failed to update task');
    } finally {
      setTaskPending((p) => {
        const copy = { ...p };
        delete copy[task.key];
        return copy;
      });
    }
  };

  const handleDelete = async () => {
    const ok = window.confirm(`Delete "${displayPrimaryPlantName(plant)}" from your library? Any markers on your maps will also be removed.`);
    if (!ok) return;
    try {
      await plantsApi.remove(plantId);
      navigate('/garden/plants');
    } catch (e) {
      alert(e.message || 'Could not delete plant');
    }
  };

  const handleUploadPhoto = async (file) => {
    if (!photoSheetSeason) return;
    setUploading(true);
    try {
      await photosApi.upload(plantId, file, { season: photoSheetSeason });
      const fresh = await photosApi.list(plantId);
      setPhotos(fresh.photos || []);
      setPhotoSheetSeason(null);
    } catch (e) {
      alert(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photo) => {
    const ok = window.confirm('Remove this photo?');
    if (!ok) return;
    try {
      await photosApi.remove(photo.id);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      setLightboxPhoto(null);
    } catch (e) {
      alert(e.message || 'Could not remove photo');
    }
  };

  if (loading) return <div className="px-4 py-6 text-sm text-garden-muted">Loading…</div>;
  if (!plant) {
    return (
      <div className="px-4 py-6">
        <div className="text-sm text-red-600 mb-3">{error || 'Plant not found.'}</div>
        <PrimaryButton variant="secondary" onClick={() => navigate('/garden/plants')}>
          <ArrowLeft size={16} /> Back
        </PrimaryButton>
      </div>
    );
  }

  const bloom = monthRange(plant.bloom_month_start, plant.bloom_month_end);
  const dormancy = monthRange(plant.dormancy_start, plant.dormancy_end);
  const titleName = displayPrimaryPlantName(plant);
  const botanicalSub = displayBotanicalSubtitle(plant);

  const basicFacts = [
    { k: 'Type',     v: plant.plant_type },
    { k: 'Variety',  v: plant.variety },
    { k: 'Nickname', v: plant.nickname },
  ].filter((f) => !!f.v);

  const appearanceFacts = [
    { k: 'Bloom color', v: plant.bloom_color },
    { k: 'Blooms',      v: bloom },
    { k: 'Foliage',     v: plant.foliage_notes },
    { k: 'Evergreen',   v: boolLabel(plant.is_evergreen) },
  ].filter((f) => !!f.v);

  const careFacts = [
    { k: 'Sun',             v: plant.sun_needs },
    { k: 'Water',           v: plant.water_needs },
    { k: 'Soil',            v: plant.soil_preference },
    { k: 'Hardiness zone',  v: plant.hardiness_zone },
    { k: 'Mature height',   v: plant.mature_height },
    { k: 'Mature width',    v: plant.mature_width },
  ].filter((f) => !!f.v);

  const seasonalFacts = [
    { k: 'Prune',     v: plant.prune_month ? MONTHS[plant.prune_month - 1] : null },
    { k: 'Fertilise', v: plant.fertilise_month ? MONTHS[plant.fertilise_month - 1] : null },
    { k: 'Divide',    v: plant.divide_month ? MONTHS[plant.divide_month - 1] : null },
    { k: 'Mulch',     v: plant.mulch_month ? MONTHS[plant.mulch_month - 1] : null },
    { k: 'Dormancy',  v: dormancy },
  ].filter((f) => !!f.v);

  const seasonTasks = SEASONS
    .map((s) => ({ s, text: plant[`${s}_tasks`] }))
    .filter((x) => !!x.text);

  const gardenFacts = [
    { k: 'Where it lives', v: plant.location_notes },
    { k: 'Date planted',   v: plant.date_planted ? plant.date_planted.slice(0, 10) : null },
    { k: 'Health',         v: plant.health_status },
    { k: 'Pet-safe',             v: boolLabel(plant.is_pet_safe) },
    { k: 'Pollinator-friendly',  v: boolLabel(plant.is_pollinator_friendly) },
    { k: 'Native',               v: boolLabel(plant.is_native) },
    { k: 'Drought-tolerant',     v: boolLabel(plant.is_drought_tolerant) },
  ].filter((f) => !!f.v);

  // Count editable detail fields still blank. Empty string, null, and
  // undefined all count as "not filled in"; booleans are only counted as
  // filled when explicitly true/false.
  const missingCount = EDITABLE_DETAIL_FIELDS.reduce((n, k) => {
    const v = plant[k];
    if (v === null || v === undefined) return n + 1;
    if (typeof v === 'string' && v.trim() === '') return n + 1;
    return n;
  }, 0);

  const isTagScan = plant.source_type === 'tag_scan';
  const rawOcr = (plant.raw_ocr_text || '').trim();

  return (
    <div className="pb-28">
      <header className="flex items-center gap-2 px-2 pt-3 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="flex-1 text-lg font-semibold text-garden-ink truncate">
          {titleName}
        </h1>
        <button
          onClick={() => navigate(`/garden/ai?plant_id=${plantId}`)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Ask AI about this plant"
          title="Ask AI"
        >
          <Sparkles size={20} />
        </button>
        <button
          onClick={() => navigate(`/garden/journal/new?plant_id=${plantId}`)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Add journal entry for this plant"
          title="Journal this"
        >
          <BookOpen size={20} />
        </button>
        <button
          onClick={() => navigate(`/garden/plants/${plantId}/edit`)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Edit plant"
        >
          <Edit2 size={20} />
        </button>
        <button
          onClick={handleDelete}
          className="min-h-11 min-w-11 -mr-2 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Delete plant"
        >
          <Trash2 size={20} />
        </button>
      </header>

      <div className="px-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-14 h-14 rounded-full bg-garden-primaryLight flex items-center justify-center text-3xl">
            <span aria-hidden>{emojiFor(plant.plant_type)}</span>
          </div>
          <div className="flex-1 min-w-0">
            {botanicalSub ? (
              <div className="text-sm italic text-garden-muted truncate">
                {botanicalSub}
              </div>
            ) : null}
            {plant.nickname ? (
              <div className="text-xs text-garden-muted truncate">
                aka {plant.nickname}
              </div>
            ) : null}
          </div>
        </div>

        {marker ? (
          <div className="mb-3 p-3 rounded-xl bg-garden-primaryLight text-sm text-garden-primaryDark flex items-center gap-2">
            <MapPin size={16} />
            <span className="flex-1">Placed on a garden area</span>
          </div>
        ) : null}

        {isTagScan ? (
          <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-garden-primary/10 text-garden-primary text-[11px] font-medium">
            <ScanLine size={12} />
            Added from a scanned tag
          </div>
        ) : null}

        {missingCount > 0 ? (
          <button
            type="button"
            onClick={() => navigate(`/garden/plants/${plantId}/edit`)}
            className="mb-3 w-full p-3 rounded-xl bg-white border border-garden-primary/20 flex items-center gap-3 text-left hover:border-garden-primary/40 transition-colors"
          >
            <div className="w-9 h-9 rounded-full bg-garden-primary/10 text-garden-primary flex items-center justify-center shrink-0">
              <Edit2 size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-garden-ink">
                {missingCount} detail{missingCount === 1 ? '' : 's'} not yet filled in
              </div>
              <div className="text-xs text-garden-muted">
                Tap to edit {'\u2014'} or use Fill with AI on the edit screen.
              </div>
            </div>
            <ChevronRight size={16} className="text-garden-muted shrink-0" />
          </button>
        ) : null}

        <section className="mb-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-garden-muted mb-2">
            Photos
          </h2>
          <SeasonalPhotoStrip
            photos={photos}
            onSlotClick={(season) => setPhotoSheetSeason(season)}
          />
          {photos.length > 0 ? (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
              {photos.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setLightboxPhoto(p)}
                  className="w-20 h-20 rounded-lg overflow-hidden bg-garden-primaryLight shrink-0 border border-black/5"
                >
                  <img src={p.image_url} alt={p.caption || ''} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          ) : null}
        </section>

        {isTagScan && (plant.source_image || rawOcr) ? (
          <section className="mb-5">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-garden-muted mb-2">
              Original plant tag
            </h2>
            <div className="rounded-xl bg-white border border-black/5 p-3 flex gap-3 items-start">
              {plant.source_image ? (
                <button
                  type="button"
                  onClick={() => setLightboxPhoto({
                    image_url: plant.source_image,
                    caption: 'Original plant tag',
                  })}
                  className="w-20 h-20 rounded-lg overflow-hidden bg-garden-primaryLight shrink-0 border border-black/5"
                  aria-label="View original plant tag"
                >
                  <img
                    src={plant.source_image}
                    alt="Scanned plant tag"
                    className="w-full h-full object-cover"
                  />
                </button>
              ) : (
                <div className="w-20 h-20 rounded-lg bg-garden-primary/10 text-garden-primary flex items-center justify-center shrink-0">
                  <ScanLine size={22} />
                </div>
              )}
              <div className="flex-1 min-w-0 text-sm text-garden-muted">
                <div className="text-garden-ink">
                  This plant was added by scanning a plant tag.
                </div>
                {rawOcr ? (
                  <button
                    type="button"
                    onClick={() => setOcrOpen((v) => !v)}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-garden-primary"
                  >
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${ocrOpen ? 'rotate-180' : ''}`}
                    />
                    {ocrOpen ? 'Hide tag text' : 'Show tag text'}
                  </button>
                ) : null}
              </div>
            </div>
            {ocrOpen && rawOcr ? (
              <pre className="mt-2 rounded-xl bg-white border border-black/5 p-3 text-xs text-garden-ink whitespace-pre-wrap font-mono leading-relaxed">
                {rawOcr}
              </pre>
            ) : null}
          </section>
        ) : null}

        <section className="mb-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-garden-muted mb-2">
            Upcoming tasks
          </h2>
          {upcomingLoading ? (
            <p className="text-xs text-garden-muted">Loading…</p>
          ) : upcoming.length === 0 ? (
            <p className="text-xs text-garden-muted">
              No upcoming tasks {'\u2014'} add prune / fertilise / seasonal details
              below to generate reminders.
            </p>
          ) : (
            <ul className="bg-white rounded-2xl border border-garden-ink/10 divide-y divide-garden-ink/5 overflow-hidden">
              {upcoming.map((t) => (
                <TaskRow
                  key={t.key}
                  task={t}
                  busy={!!taskPending[t.key]}
                  showPlantName={false}
                  onToggle={() => toggleTask(t)}
                />
              ))}
            </ul>
          )}
        </section>

        <section className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-garden-muted">
              Journal entries
            </h2>
            {journalEntries.length > 3 ? (
              <button
                type="button"
                onClick={() => navigate(`/garden/journal?plant_id=${plantId}`)}
                className="text-xs font-medium text-garden-primary"
              >
                View all ({journalEntries.length})
              </button>
            ) : null}
          </div>

          {journalLoading ? (
            <p className="text-xs text-garden-muted">Loading{'\u2026'}</p>
          ) : journalEntries.length === 0 ? (
            <div className="rounded-xl bg-white border border-black/5 p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-garden-primary/10 text-garden-primary flex items-center justify-center shrink-0">
                <BookOpen size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-garden-ink">No entries yet for this plant.</div>
                <button
                  type="button"
                  onClick={() => navigate(`/garden/journal/new?plant_id=${plantId}`)}
                  className="mt-0.5 text-xs font-medium text-garden-primary underline underline-offset-2"
                >
                  Add the first one
                </button>
              </div>
            </div>
          ) : (
            <ul className="space-y-2">
              {journalEntries.slice(0, 3).map((entry) => (
                <JournalPreviewRow
                  key={entry.id}
                  entry={entry}
                  onOpen={() => navigate(`/garden/journal/${entry.id}`)}
                />
              ))}
            </ul>
          )}
        </section>

        <FactList title="Basics" facts={basicFacts} />
        <FactList title="Appearance" facts={appearanceFacts} />
        <FactList title="Care" facts={careFacts} />
        <FactList title="Seasonal" facts={seasonalFacts} />

        {seasonTasks.length > 0 ? (
          <section className="mb-5">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-garden-muted mb-2">
              Things to do
            </h2>
            <div className="space-y-2">
              {seasonTasks.map(({ s, text }) => (
                <div key={s} className="rounded-xl bg-white border border-black/5 p-3">
                  <div className="text-xs font-medium text-garden-primaryDark capitalize mb-1">
                    {s}
                  </div>
                  <div className="text-sm text-garden-ink whitespace-pre-wrap">{text}</div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <FactList title="In my garden" facts={gardenFacts} />

        {plant.prune_notes ? (
          <section className="mb-5">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-garden-muted mb-2">
              Prune notes
            </h2>
            <div className="rounded-xl bg-white border border-black/5 p-3 text-sm text-garden-ink whitespace-pre-wrap">
              {plant.prune_notes}
            </div>
          </section>
        ) : null}
      </div>

      <BottomSheet
        open={!!photoSheetSeason}
        onClose={() => { if (!uploading) setPhotoSheetSeason(null); }}
        title={`Add a ${photoSheetSeason || ''} photo`}
      >
        <div className="space-y-3">
          <p className="text-sm text-garden-muted">
            Pick a photo from your device for the {photoSheetSeason} season.
          </p>
          <PhotoUploader onPick={handleUploadPhoto} busy={uploading} label="Choose photo" />
        </div>
      </BottomSheet>

      {lightboxPhoto ? (
        <div
          className="fixed inset-0 z-40 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 text-white min-h-11 min-w-11 rounded-full flex items-center justify-center"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <img
            src={lightboxPhoto.image_url}
            alt={lightboxPhoto.caption || ''}
            className="max-h-[80vh] max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {lightboxPhoto.id ? (
            <button
              onClick={(e) => { e.stopPropagation(); handleDeletePhoto(lightboxPhoto); }}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-600 text-white rounded-full text-sm"
            >
              <span className="inline-flex items-center gap-2"><Trash2 size={14} /> Remove photo</span>
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="fixed bottom-16 left-0 right-0 z-20 bg-white/95 backdrop-blur border-t border-black/5 p-3">
        <div className="mx-auto max-w-md flex gap-2">
          <PrimaryButton
            variant="secondary"
            onClick={() => setPhotoSheetSeason('spring')}
            className="flex-1"
          >
            <Camera size={16} /> Add photo
          </PrimaryButton>
          <PrimaryButton
            onClick={() => navigate(`/garden/plants/${plantId}/edit`)}
            className="flex-1"
          >
            <Edit2 size={16} /> Edit details
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function JournalPreviewRow({ entry, onOpen }) {
  const photos = entry.photo_urls || [];
  const firstPhoto = photos[0];
  const dateLabel = entry.entry_date
    ? `${MONTHS[new Date(entry.entry_date).getMonth()]} ${new Date(entry.entry_date).getDate()}`
    : '';
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
      }}
      className="bg-white rounded-2xl border border-black/5 p-3 cursor-pointer hover:border-garden-primary/40 focus:border-garden-primary/40 focus:outline-none transition-colors flex gap-3 items-start"
    >
      {firstPhoto ? (
        <div className="w-14 h-14 rounded-lg overflow-hidden bg-garden-primaryLight shrink-0">
          <img src={firstPhoto} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-14 h-14 rounded-lg bg-garden-primary/10 text-garden-primary flex items-center justify-center shrink-0">
          <BookOpen size={20} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide font-semibold text-garden-primary">
          <span>{dateLabel}</span>
          {photos.length > 1 ? (
            <span className="text-garden-muted normal-case tracking-normal font-normal">
              {photos.length} photos
            </span>
          ) : null}
        </div>
        {entry.body_text ? (
          <p className="mt-0.5 text-sm text-garden-ink line-clamp-2 whitespace-pre-wrap">
            {entry.body_text}
          </p>
        ) : (
          <p className="mt-0.5 text-sm italic text-garden-muted">(photo-only entry)</p>
        )}
      </div>
    </li>
  );
}

function FactList({ title, facts }) {
  if (!facts || facts.length === 0) return null;
  return (
    <section className="mb-5">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-garden-muted mb-2">
        {title}
      </h2>
      <dl className="rounded-xl bg-white border border-black/5 divide-y divide-black/5">
        {facts.map((f) => (
          <div key={f.k} className="flex gap-3 px-3 py-2.5 text-sm">
            <dt className="min-w-[40%] text-garden-muted">{f.k}</dt>
            <dd className="flex-1 text-garden-ink capitalize">{f.v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
